import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../platform';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui';
import { Spinner } from './ui/spinner';
import { Check } from 'lucide-react';
import logoIcon from '../img/logo_icon.png';

/**
 * Full-page login screen for BuyTelegramAds
 * Shown when users visit /app without being authenticated
 */
export function LoginPage() {
  const { loginWithTelegram } = useAuth();
  const [loginError, setLoginError] = useState(null);
  const [botUsername, setBotUsername] = useState(null);

  // Stable callback for Telegram auth
  const handleTelegramAuth = useCallback(async (user) => {
    setLoginError(null);
    const success = await loginWithTelegram(user);
    if (!success) {
      setLoginError('Login failed. Please try again.');
    }
    // On success, AuthProvider will update isAuthenticated and App.js will show dashboard
  }, [loginWithTelegram]);

  // Fetch bot config on mount
  useEffect(() => {
    fetch('/api/config')
      .then(res => res.json())
      .then(data => {
        if (data.bot_username) {
          setBotUsername(data.bot_username);
        }
      })
      .catch(() => {
        // Fallback
        setBotUsername('BuyTelegramAdsBot');
      });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 via-background to-background">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="w-full max-w-md relative">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center justify-center gap-2 mb-2 hover:opacity-90 transition-opacity">
            <img src={logoIcon} alt="BuyTelegramAds" className="h-8 w-8 object-contain" />
            <span className="text-3xl font-bold text-foreground">BuyTelegramAds</span>
          </a>
          <p className="text-muted-foreground">Pay for Telegram Ads in EUR</p>
        </div>

        <Card className="shadow-xl">
          <CardHeader className="text-center">
            <CardTitle>Login to Continue</CardTitle>
            <CardDescription>
              Connect your Telegram account to access the dashboard
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Login Widget */}
            <div className="flex justify-center">
              {botUsername ? (
                <TelegramLoginButton
                  botName={botUsername}
                  onAuth={handleTelegramAuth}
                  onError={() => setLoginError('Failed to load login widget. Please refresh the page.')}
                />
              ) : (
                <Spinner className="h-8 w-8 text-primary" />
              )}
            </div>

            {loginError && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm text-center">
                {loginError}
              </div>
            )}

            {/* Features */}
            <div className="space-y-3 pt-4 border-t">
              <h3 className="text-sm font-medium text-center text-muted-foreground">
                After logging in, you'll be able to:
              </h3>
              <ul className="space-y-2">
                {[
                  'Connect your Telegram channels',
                  'Top up your Telegram Ads balance in EUR',
                  'Track your top-up history',
                  'Access your Telegram Ads office',
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check size={16} className="text-primary shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        {<div className="mt-6 text-center text-xs text-muted-foreground">
          <p>
            By logging in, you agree to our{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Terms of Service
            </a>
            {' '}and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Privacy Policy
            </a>
          </p>
          <p className="mt-2">
            <a href="/" className="text-primary hover:underline">Back to homepage</a>
          </p>
        </div>}
      </div>
    </div>
  );
}

/**
 * Telegram Login Button component
 * Creates the Telegram Login Widget
 */
function TelegramLoginButton({ botName, onAuth, onError }) {
  const containerRef = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    // Prevent double-mounting in strict mode
    if (mountedRef.current) return;
    mountedRef.current = true;

    const container = containerRef.current;
    if (!container) return;

    // Set up the global callback
    window.TelegramLoginWidget = {
      dataOnauth: (user) => onAuth(user)
    };

    // Create script element
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', botName);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-onauth', 'TelegramLoginWidget.dataOnauth(user)');
    script.setAttribute('data-request-access', 'write');
    script.async = true;
    script.onerror = onError;

    container.appendChild(script);

    // No cleanup - let the widget remain since removing it causes issues
  }, [botName, onAuth, onError]);

  return (
    <div
      ref={containerRef}
      className="min-h-[48px] flex justify-center"
    />
  );
}

export default LoginPage;
